/**
 * 여러 API 로 흩어진 저장 흐름을 다루는 공통 도구.
 *
 * 원래 쇼핑몰 디자인 화면(MallDesign) 안에만 있었는데, 포트폴리오 저장도
 * "포트폴리오 본문 → 버튼 → 연결 주문"으로 단계가 나뉘면서 같은 문제를
 * 그대로 겪게 되어 공용으로 옮겼다. 같은 헬퍼를 페이지마다 다시 적어두면
 * 실패 메시지 규칙이 화면마다 갈린다.
 */

/**
 * 서버가 내려준 실패 사유를 꺼낸다. 없으면 fallback.
 *
 * usecase 가 `new AppError(String(error), code, error)` 로 감싸기 때문에
 * AppError.message 는 "Error: ..." 처럼 접두사가 붙는다. 진짜 서버 메시지
 * (api-service 가 errorMsg 로 만든 Error) 는 originalError 에 들어 있다.
 */
export const resolveErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const original = (error as { originalError?: unknown })?.originalError;
  const message =
    original instanceof Error
      ? original.message
      : error instanceof Error
        ? error.message
        : null;
  return message?.trim() || fallback;
};

/* ------------------------------------------------------------------ *
 * 저장 흐름
 * ------------------------------------------------------------------ */

/**
 * MallDesign 의 저장은 여러 API 로 흩어져 있고 백엔드에 이들을 묶는 트랜잭션이
 * 없다. 그래서 중간에 하나가 실패하면 앞의 것은 이미 저장된 부분 저장 상태가
 * 남는다. 전부 되돌리는 롤백은 백엔드 트랜잭션 없이는 구현할 수 없다.
 *
 * 그래서 여기서는 롤백 대신 **어디까지 저장됐는지 정확히 알리는 것**을 한다.
 * 사용자가 "배너는 저장됐는데 띠배너가 안 됐다"를 알면 직접 고칠 수 있다.
 */
export interface SaveStep {
  /** 사용자에게 보일 단계 이름 */
  label: string;
  run: () => Promise<void>;
}

export interface SaveStepsResult {
  /** 성공적으로 끝난 단계 이름 (실행 순서대로) */
  completed: string[];
  /** 실패한 단계. 없으면 전부 성공 */
  failed: { label: string; error: unknown } | null;
}

/**
 * 단계를 순서대로 실행하고 첫 실패에서 멈춘다.
 *
 * 멈추는 이유: 뒤 단계를 계속 시도하면 부분 저장 범위가 더 넓어지기만 하고,
 * 사용자에게 알려야 할 상태도 복잡해진다. 앞에서 멈춰야 "어디까지 됐는지"가
 * 단순하게 유지된다.
 */
export const runSaveSteps = async (
  steps: SaveStep[],
): Promise<SaveStepsResult> => {
  const completed: string[] = [];

  for (const step of steps) {
    try {
      await step.run();
      completed.push(step.label);
    } catch (error) {
      console.error(`[MallDesign] '${step.label}' 저장 실패`, error);
      return { completed, failed: { label: step.label, error } };
    }
  }

  return { completed, failed: null };
};

/** 저장 결과를 사용자에게 보여줄 한 줄로 만든다 */
export const buildSaveResultMessage = (result: SaveStepsResult): string => {
  if (!result.failed) return "저장되었습니다.";

  const reason = resolveErrorMessage(result.failed.error, "알 수 없는 오류");
  const head = `${result.failed.label} 저장에 실패했습니다 (${reason}).`;

  if (result.completed.length === 0) {
    return `${head} 저장된 항목은 없습니다.`;
  }

  /*
    롤백이 없으므로 앞 단계는 저장된 채로 남는다. 그 사실을 분명히 알린다.
    단계 이름 뒤에 조사를 붙이면 받침에 따라 은/는이 갈리므로 목록 형태로 쓴다.
  */
  return `${head} 이미 저장된 항목: ${result.completed.join(", ")}. 되돌리려면 직접 수정해야 합니다.`;
};
